// src/components/AIInsights.jsx
// AI-powered trading performance analysis
// Calls Supabase Edge Function — API key stays server-side, never in browser
import React, { useState, useCallback } from 'react';
import { Sparkles, RefreshCw, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function AIInsights({ trades, settings, odfScore, patternData }) {
  const [report,   setReport]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [expanded, setExpanded] = useState(false);
  const [hoursLeft,setHoursLeft]= useState(0);

  const closed   = (trades || []).filter(t => t.status !== 'open');
  const startBal = settings?.startingBalance || 0;

  const buildPrompt = useCallback(() => {
    const wins    = closed.filter(t => t.result === 'Win');
    const totalPnl = closed.reduce((s, t) => s + Number(t.pnl || 0), 0);
    const rrs      = closed.filter(t => t.rrAchieved).map(t => Number(t.rrAchieved));
    const avgRR    = rrs.length ? (rrs.reduce((a,b)=>a+b,0)/rrs.length).toFixed(2) : 'N/A';
    const followed = closed.filter(t => t.followedRules === true || t.followedRules === 'true').length;

    const recentTrades = [...closed]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 20)
      .map(t => `${t.date} | ${t.pair} | ${t.direction} | ${t.result} | P&L: $${Number(t.pnl||0).toFixed(2)} | RR: ${t.rrAchieved||'N/A'} | Rules: ${t.followedRules?'Yes':'No'} | Mistake: ${t.mistakeMade||'None'} | Emotion: ${t.emotionBefore||'N/A'}`)
      .join('\n');

    const topPatterns = (patternData?.insights || []).slice(0, 3)
      .map(p => `- ${p.title}: ${p.detail}`)
      .join('\n') || 'Insufficient data for patterns';

    return `You are a professional trading performance coach analysing a trader's journal data from ODFTrades.

TRADER STATS:
- Total closed trades: ${closed.length}
- Win rate: ${closed.length > 0 ? Math.round((wins.length / closed.length) * 100) : 0}%
- Total P&L: $${totalPnl.toFixed(2)}
- Average RR: ${avgRR}R
- Rule adherence: ${closed.length > 0 ? Math.round((followed / closed.length) * 100) : 0}%
- ODF Score: ${odfScore?.total || 'N/A'}/100 (Grade: ${odfScore?.grade || 'N/A'} — ${odfScore?.label || 'N/A'})
${startBal > 0 ? `- Starting balance: $${startBal}\n- ROI: ${((totalPnl/startBal)*100).toFixed(2)}%` : ''}

KEY PATTERNS DETECTED:
${topPatterns}

RECENT 20 TRADES (newest first):
${recentTrades}

Write a concise, direct, and honest performance analysis. Structure it EXACTLY as:

**PERFORMANCE SUMMARY**
2-3 sentences on overall performance.

**YOUR EDGE**
What is working. Be specific with numbers from their data.

**BIGGEST WEAKNESS**
The one thing most costing them money. Be direct and specific.

**3 ACTIONABLE STEPS**
Three specific things to do differently. Reference their actual data — no generic advice.

**MINDSET NOTE**
One honest observation about their discipline and emotional patterns.

Under 350 words total. Be direct like a coach. Every point must reference their actual numbers.`;
  }, [closed, settings, odfScore, patternData]);

  const generateReport = async () => {
    if (loading || closed.length < 5) return;
    setLoading(true); setError(''); setReport(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Use the singleton supabase client's URL + key — no hardcoded duplicates.
      const SUPABASE_URL = supabase.supabaseUrl;
      const SUPABASE_KEY = supabase.supabaseKey;
      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/ai-report`,
        {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey':        SUPABASE_KEY,
          },
          body: JSON.stringify({ prompt: buildPrompt() }),
        }
      );

      const data = await res.json();

      if (res.status === 429) {
        setHoursLeft(data.hoursLeft || 24);
        setError(data.error || 'Rate limited. Come back tomorrow for your next report.');
        setLoading(false);
        return;
      }

      if (!res.ok || data.error) throw new Error(data.error || 'Failed to generate report');

      setReport(data.report);
      setHoursLeft(0);
      setExpanded(true);
    } catch (e) {
      setError(e.message || 'Could not generate report. Please try again later.');
    }
    setLoading(false);
  };

  if (closed.length < 5) {
    return (
      <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:'20px 24px', marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
          <Sparkles size={16} style={{ color:'#00e5a0' }} />
          <div style={{ fontSize:14, fontWeight:600, color:'#f0f0f5', fontFamily:"'IBM Plex Sans',sans-serif" }}>AI Performance Coach</div>
          <div style={{ fontSize:10, color:'#00e5a0', background:'rgba(0,229,160,0.08)', border:'1px solid rgba(0,229,160,0.15)', borderRadius:5, padding:'2px 8px', fontFamily:"'DM Mono',monospace" }}>Pro Feature</div>
        </div>
        <div style={{ fontSize:13, color:'#55556a' }}>Log 5+ trades to unlock your Performance Coach.</div>
      </div>
    );
  }

  return (
    <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, overflow:'hidden', marginBottom:16 }}>
      {/* Header */}
      <div style={{ padding:'18px 24px', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <div style={{ width:34, height:34, borderRadius:9, background:'rgba(0,229,160,0.1)', border:'1px solid rgba(0,229,160,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <Sparkles size={16} style={{ color:'#00e5a0' }} />
        </div>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <div style={{ fontSize:14, fontWeight:600, color:'#f0f0f5', fontFamily:"'IBM Plex Sans',sans-serif" }}>Performance Coach</div>
            <div style={{ fontSize:10, color:'#00e5a0', background:'rgba(0,229,160,0.08)', border:'1px solid rgba(0,229,160,0.15)', borderRadius:5, padding:'2px 8px', fontFamily:"'DM Mono',monospace" }}>Performance Coach</div>
          </div>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", marginTop:2 }}>
            {closed.length} trades analysed · Personalised to your data · Updates daily
          </div>
        </div>

        <div style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
          {report && (
            <button onClick={() => setExpanded(e => !e)} style={{ background:'transparent', border:'1px solid #1e1e2e', borderRadius:7, padding:'7px 12px', cursor:'pointer', color:'#8888a8', display:'flex', alignItems:'center', gap:5, fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='#2a2a3e';e.currentTarget.style.color='#f0f0f5';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='#1e1e2e';e.currentTarget.style.color='#8888a8';}}>
              {expanded ? <><ChevronUp size={12}/> Hide</> : <><ChevronDown size={12}/> View Report</>}
            </button>
          )}
          <button onClick={generateReport} disabled={loading || hoursLeft > 0} style={{
            display:'flex', alignItems:'center', gap:7,
            background: hoursLeft > 0 ? 'transparent' : 'rgba(0,229,160,0.1)',
            border: `1px solid ${hoursLeft > 0 ? '#1e1e2e' : 'rgba(0,229,160,0.3)'}`,
            borderRadius:8, padding:'9px 18px', fontSize:13, fontWeight:600,
            color: hoursLeft > 0 ? '#55556a' : '#00e5a0',
            cursor: (loading || hoursLeft > 0) ? 'not-allowed' : 'pointer',
            fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s',
          }}>
            {loading ? (
              <><span style={{ width:13, height:13, borderRadius:'50%', border:'2px solid rgba(0,229,160,0.3)', borderTopColor:'#00e5a0', display:'inline-block', animation:'spin 0.7s linear infinite' }} /> Analysing...</>
            ) : hoursLeft > 0 ? (
              <><RefreshCw size={12}/> Next in {hoursLeft}h</>
            ) : (
              <><Sparkles size={13}/> {report ? 'Regenerate' : 'Get Coaching Report'}</>
            )}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding:'0 24px 14px', display:'flex', gap:8, alignItems:'flex-start' }}>
          <AlertCircle size={14} style={{ color:'#ff4d6d', flexShrink:0, marginTop:1 }} />
          <div style={{ fontSize:12, color:'#ff4d6d', lineHeight:1.6 }}>{error}</div>
        </div>
      )}

      {/* Report */}
      {report && expanded && (
        <div style={{ borderTop:'1px solid #1e1e2e', padding:'20px 24px' }}>
          <div style={{ fontFamily:"'IBM Plex Sans',sans-serif" }}>
            {report.split('\n').map((line, i) => {
              const isBold = line.startsWith('**') && line.endsWith('**');
              if (isBold) return (
                <div key={i} style={{ fontSize:11, fontWeight:700, color:'#00e5a0', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.09em', marginTop: i > 0 ? 20 : 0, marginBottom:8 }}>
                  {line.replace(/\*\*/g, '')}
                </div>
              );
              if (!line.trim()) return <div key={i} style={{ height:4 }} />;
              return <div key={i} style={{ fontSize:13, color:'#8888a8', lineHeight:1.85, marginBottom:2 }}>{line}</div>;
            })}
          </div>
          <div style={{ marginTop:20, paddingTop:12, borderTop:'1px solid #1e1e2e', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
            <div style={{ fontSize:10, color:'#2a2a3e', fontFamily:"'DM Mono',monospace" }}>
              Analysis based on your logged data · Not financial advice
            </div>
            <div style={{ fontSize:10, color:'#2a2a3e', fontFamily:"'DM Mono',monospace" }}>
              Regenerates every 24 hours
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
